/**
 * Import Preview component - Shows parsed dishes with validation status
 */

import React, { useState, useMemo, useCallback } from 'react';
import Table from '../comman/Table.js';
import Badge from '../comman/Badge.js';
import Button from '../comman/button.js';
import Input from '../comman/input.js';
import { useLanguage } from '../../utils/languageContext.js';
import { getTranslation } from '../../utils/translate.js';
import { exportErrorReport } from '../../utils/exportUtils.js';

/**
 * ImportPreview component
 * @param {Object} props - Component props
 * @param {Array} props.dishes - Array of parsed dish data
 * @param {Array} props.validationResults - Array of validation results
 * @param {Set} props.selectedRows - Set of selected row indices
 * @param {Function} props.onRowSelect - Callback when row selection changes
 * @param {Function} props.onSelectAll - Callback when select all changes
 * @param {Function} props.onDeselectAll - Callback when deselect all is clicked
 */
const ImportPreview = ({
  dishes = [],
  validationResults = [],
  selectedRows = new Set(),
  onRowSelect,
  onSelectAll,
  onDeselectAll
}) => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, valid, invalid, warning

  // Memoized filtered and searched dishes with original indices
  const filteredDishesWithIndices = useMemo(() => {
    let filtered = dishes.map((dish, index) => ({ dish, originalIndex: index }));

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(({ dish, originalIndex }) => {
        const validation = validationResults[originalIndex];
        return (
          dish.nameHy?.toLowerCase().includes(search) ||
          dish.nameEn?.toLowerCase().includes(search) ||
          dish.nameRu?.toLowerCase().includes(search) ||
          validation?.errors?.some(e => e.message.toLowerCase().includes(search)) ||
          validation?.warnings?.some(w => w.message.toLowerCase().includes(search))
        );
      });
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(({ dish, originalIndex }) => {
        const validation = validationResults[originalIndex];
        if (!validation) return false;

        if (filterStatus === 'valid') {
          return validation.isValid && validation.warnings.length === 0;
        } else if (filterStatus === 'invalid') {
          return !validation.isValid;
        } else if (filterStatus === 'warning') {
          return validation.warnings.length > 0;
        }
        return true;
      });
    }

    return filtered;
  }, [dishes, validationResults, searchTerm, filterStatus]);

  // Extract just the dishes for display
  const filteredDishes = useMemo(() => 
    filteredDishesWithIndices.map(({ dish }) => dish),
    [filteredDishesWithIndices]
  );

  // Helper function to get original index from filtered index
  const getOriginalIndex = useCallback((filteredIndex) => {
    return filteredDishesWithIndices[filteredIndex]?.originalIndex;
  }, [filteredDishesWithIndices]);

  // Statistics
  const stats = useMemo(() => {
    const total = dishes.length;
    const valid = validationResults.filter(r => r.isValid && r.warnings.length === 0).length;
    const invalid = validationResults.filter(r => !r.isValid).length;
    const withWarnings = validationResults.filter(r => r.warnings.length > 0).length;
    const selected = selectedRows.size;

    return { total, valid, invalid, withWarnings, selected };
  }, [dishes.length, validationResults, selectedRows.size]);

  // Table columns
  const columns = useMemo(() => [
    {
      key: 'status',
      label: getTranslation('import.status', language) || 'Status',
      width: '120px',
      minWidth: '100px',
      render: (row, filteredIndex) => {
        const originalIndex = getOriginalIndex(filteredIndex);
        const validation = originalIndex !== undefined ? validationResults[originalIndex] : null;
        if (!validation) {
          return <Badge variant="default" size="sm">{getTranslation('import.unknown', language) || 'Unknown'}</Badge>;
        }

        if (!validation.isValid) {
          return <Badge variant="error" size="sm">{getTranslation('import.invalid', language) || 'Invalid'}</Badge>;
        }

        if (validation.warnings.length > 0) {
          return <Badge variant="warning" size="sm">{getTranslation('import.warning', language) || 'Warning'}</Badge>;
        }

        return <Badge variant="success" size="sm">{getTranslation('import.valid', language) || 'Valid'}</Badge>;
      }
    },
    {
      key: 'name',
      label: getTranslation('import.name', language) || 'Name',
      width: '200px',
      render: (row) => (
        <div className="font-medium">
          {row.nameHy || '-'}
        </div>
      )
    },
    {
      key: 'type',
      label: getTranslation('import.type', language) || 'Type',
      width: '120px',
      render: (row) => row.type || '-'
    },
    {
      key: 'price',
      label: getTranslation('import.price', language) || 'Price',
      width: '100px',
      render: (row) => row.price ? `${row.price} AMD` : '-'
    },
    {
      key: 'errors',
      label: getTranslation('import.errors', language) || 'Errors',
      width: '200px',
      render: (row, filteredIndex) => {
        const originalIndex = getOriginalIndex(filteredIndex);
        const validation = originalIndex !== undefined ? validationResults[originalIndex] : null;
        if (!validation || validation.errors.length === 0) {
          return <span className="text-gray-400 dark:text-[#8b949e]">-</span>;
        }

        return (
          <div className="text-xs">
            {validation.errors.slice(0, 2).map((error, i) => (
              <div key={i} className="text-red-600 dark:text-red-400 truncate" title={error.message}>
                {error.message}
              </div>
            ))}
            {validation.errors.length > 2 && (
              <div className="text-gray-500 dark:text-[#8b949e]">
                +{validation.errors.length - 2} {getTranslation('import.moreErrors', language).replace('{count}', '') || 'more'}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'warnings',
      label: getTranslation('import.warnings', language) || 'Warnings',
      width: '200px',
      render: (row, filteredIndex) => {
        const originalIndex = getOriginalIndex(filteredIndex);
        const validation = originalIndex !== undefined ? validationResults[originalIndex] : null;
        if (!validation || validation.warnings.length === 0) {
          return <span className="text-gray-400 dark:text-[#8b949e]">-</span>;
        }

        return (
          <div className="text-xs">
            {validation.warnings.slice(0, 2).map((warning, i) => (
              <div key={i} className="text-yellow-600 dark:text-yellow-400 truncate" title={warning.message}>
                {warning.message}
              </div>
            ))}
            {validation.warnings.length > 2 && (
              <div className="text-gray-500 dark:text-[#8b949e]">
                +{validation.warnings.length - 2} more
              </div>
            )}
          </div>
        );
      }
    }
  ], [language, validationResults, getOriginalIndex]);

  // Handle select all - use original indices from filteredDishesWithIndices
  const handleSelectAll = useCallback(() => {
    if (onSelectAll) {
      const allIndices = filteredDishesWithIndices.map(({ originalIndex }) => originalIndex);
      onSelectAll(allIndices);
    }
  }, [filteredDishesWithIndices, onSelectAll]);

  // Handle deselect all
  const handleDeselectAll = useCallback(() => {
    if (onDeselectAll) {
      onDeselectAll();
    }
  }, [onDeselectAll]);

  // Handle export error report
  const handleExportErrorReport = useCallback(() => {
    exportErrorReport(validationResults, dishes, 'import_error_report');
  }, [validationResults, dishes]);

  return (
    <div className="space-y-4">
      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <div className="text-sm text-gray-500 dark:text-[#8b949e]">
            {getTranslation('import.total', language) || 'Total'}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-[#c9d1d9]">
            {stats.total}
          </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <div className="text-sm text-gray-500 dark:text-[#8b949e]">
            {getTranslation('import.valid', language) || 'Valid'}
          </div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {stats.valid}
          </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <div className="text-sm text-gray-500 dark:text-[#8b949e]">
            {getTranslation('import.invalid', language) || 'Invalid'}
          </div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {stats.invalid}
          </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <div className="text-sm text-gray-500 dark:text-[#8b949e]">
            {getTranslation('import.warnings', language) || 'Warnings'}
          </div>
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {stats.withWarnings}
          </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <div className="text-sm text-gray-500 dark:text-[#8b949e]">
            {getTranslation('import.selected', language) || 'Selected'}
          </div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {stats.selected}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="text"
            name="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={getTranslation('import.searchPlaceholder', language) || 'Search dishes...'}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterStatus === 'all' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            {getTranslation('import.all', language) || 'All'}
          </Button>
          <Button
            variant={filterStatus === 'valid' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilterStatus('valid')}
          >
            {getTranslation('import.valid', language) || 'Valid'}
          </Button>
          <Button
            variant={filterStatus === 'invalid' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilterStatus('invalid')}
          >
            {getTranslation('import.invalid', language) || 'Invalid'}
          </Button>
          <Button
            variant={filterStatus === 'warning' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilterStatus('warning')}
          >
            {getTranslation('import.warnings', language) || 'Warnings'}
          </Button>
        </div>
      </div>

      {/* Selection Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSelectAll}
        >
          {getTranslation('import.selectAll', language) || 'Select All'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDeselectAll}
        >
          {getTranslation('import.deselectAll', language) || 'Deselect All'}
        </Button>
        {(stats.invalid > 0 || stats.withWarnings > 0) && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportErrorReport}
            className="ml-auto"
          >
            <svg
              className="w-4 h-4 mr-2 inline"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            {getTranslation('import.exportErrorReport', language) || 'Export Error Report'}
          </Button>
        )}
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={filteredDishes}
        onRowSelect={(row, filteredIndex) => {
          // Get original index from filteredDishesWithIndices
          const item = filteredDishesWithIndices[filteredIndex];
          if (item && onRowSelect) {
            const { originalIndex } = item;
            const isCurrentlySelected = selectedRows.has(originalIndex);
            onRowSelect(row, originalIndex, !isCurrentlySelected);
          }
        }}
        selectedRows={new Set(
          filteredDishesWithIndices
            .map(({ originalIndex }) => originalIndex)
            .filter(idx => idx !== undefined && selectedRows.has(idx))
        )}
        selectable={true}
        virtualized={filteredDishes.length > 50}
        rowHeight={60}
        maxHeight={600}
      />
    </div>
  );
};

export default ImportPreview;
